import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { useAuth } from '../../contexts/AuthContext';
import { exportPayoutCsv } from '../../lib/csv-export';

interface PayoutLine {
  subSellerId: string;
  subSellerName: string;
  payinSharePercent: number;
  payoutSharePercent: number;
  payinAmount: number;
  payoutAmount: number;
  status: 'unpaid' | 'paid';
  paymentMethod: string | null;
  paidAt: string | null;
}

interface PayoutItem {
  id: string;
  period: string;
  startDate: string;
  endDate: string;
  totalPayinAmount: number;
  totalPayoutAmount: number;
  payinSharingAmount: number;
  payoutSharingAmount: number;
  status: 'unpaid' | 'paid';
  paymentMethod: string | null;
  paidAt: string | null;
  generatedAt: string;
  lines: PayoutLine[];
}

export function PayoutReports() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [error, setError] = useState('');
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set());

  const monthOptions: { value: string; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    monthOptions.push({ value, label });
  }

  const getDateRange = (yearMonth: string) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const lastDay = new Date(year, month, 0);
    return {
      startDate: `${yearMonth}-01`,
      endDate: `${year}-${String(month).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`,
    };
  };

  const { startDate, endDate } = getDateRange(selectedMonth);

  const { data, isLoading } = useQuery({
    queryKey: ['seller', 'payouts'],
    queryFn: () =>
      apiGet<{ items: PayoutItem[]; total: number }>('/seller/payouts', { pageSize: '50' }),
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      apiPost('/seller/payouts/generate', {
        sellerId: user?.sellerId || '',
        period: 'monthly',
        startDate,
        endDate,
      }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'payouts'] });
        setError('');
      } else {
        setError(res.error.message);
      }
    },
  });

  const markLinePaidMutation = useMutation({
    mutationFn: ({
      payoutId,
      subSellerId,
      paymentMethod,
    }: {
      payoutId: string;
      subSellerId: string;
      paymentMethod: string;
    }) => apiPatch(`/seller/payouts/${payoutId}/mark-line-paid`, { subSellerId, paymentMethod }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'payouts'] });
      } else {
        setError(res.error.message);
      }
    },
  });

  const toggleExpand = (id: string) => {
    setExpandedPayouts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const payouts = data?.ok ? data.data.items : [];

  const formatNum = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      {/* Generate payout form */}
      <Card>
        <CardHeader>
          <CardTitle>{t('payouts.generate')}</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
              {error}
            </div>
          )}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('payouts.period')}
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <span className="pb-2 text-xs text-gray-500 dark:text-gray-400">
              {startDate} ~ {endDate}
            </span>
            <Button onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending}>
              {generateMutation.isPending ? t('common.loading') : t('payouts.generate')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payouts table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('payouts.history')}</CardTitle>
            {payouts.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => exportPayoutCsv(payouts, t)}>
                {t('payouts.exportCsv')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && payouts.length === 0 && (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          )}
          {payouts.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{''}</TableHead>
                    <TableHead>{t('payouts.periodStart')}</TableHead>
                    <TableHead>{t('payouts.periodEnd')}</TableHead>
                    <TableHead className="text-right text-blue-600 dark:text-blue-400">
                      {t('transactions.calcPayinFee')}
                    </TableHead>
                    <TableHead className="text-right text-green-600 dark:text-green-400">
                      {t('transactions.calcPayoutFee')}
                    </TableHead>
                    <TableHead className="text-right text-blue-600 dark:text-blue-400">
                      {t('payouts.payinSharing')}
                    </TableHead>
                    <TableHead className="text-right text-green-600 dark:text-green-400">
                      {t('payouts.payoutSharing')}
                    </TableHead>
                    <TableHead>{t('payouts.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((payout) => {
                    const isExpanded = expandedPayouts.has(payout.id);
                    const paidCount = payout.lines.filter((l) => l.status === 'paid').length;
                    return (
                      <>
                        <TableRow
                          key={payout.id}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                          onClick={() => toggleExpand(payout.id)}
                        >
                          <TableCell className="font-mono text-sm">
                            <span className="mr-1">{isExpanded ? '▼' : '▶'}</span>
                            {payout.period}
                          </TableCell>
                          <TableCell>{payout.startDate}</TableCell>
                          <TableCell>{payout.endDate}</TableCell>
                          <TableCell className="text-right text-blue-600 dark:text-blue-400">
                            {formatNum(payout.totalPayinAmount)}
                          </TableCell>
                          <TableCell className="text-right text-green-600 dark:text-green-400">
                            {formatNum(payout.totalPayoutAmount)}
                          </TableCell>
                          <TableCell className="text-right font-medium text-blue-600 dark:text-blue-400">
                            {formatNum(payout.payinSharingAmount)}
                          </TableCell>
                          <TableCell className="text-right font-medium text-green-600 dark:text-green-400">
                            {formatNum(payout.payoutSharingAmount)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={payout.status === 'paid' ? 'success' : 'warning'}>
                              {t(`payouts.${payout.status}`)}{' '}
                              {payout.lines.length > 0 && `(${paidCount}/${payout.lines.length})`}
                            </Badge>
                          </TableCell>
                        </TableRow>
                        {isExpanded &&
                          payout.lines.map((line) => (
                            <TableRow
                              key={`${payout.id}-${line.subSellerId}`}
                              className="bg-gray-50 dark:bg-gray-800/50"
                            >
                              <TableCell className="pl-8 font-medium">
                                {line.subSellerName}
                              </TableCell>
                              <TableCell className="text-xs text-gray-500">
                                {line.payinSharePercent}% / {line.payoutSharePercent}%
                              </TableCell>
                              <TableCell>{''}</TableCell>
                              <TableCell className="text-right text-blue-600 dark:text-blue-400">
                                {formatNum(line.payinAmount)}
                              </TableCell>
                              <TableCell className="text-right text-green-600 dark:text-green-400">
                                {formatNum(line.payoutAmount)}
                              </TableCell>
                              <TableCell>{''}</TableCell>
                              <TableCell>{''}</TableCell>
                              <TableCell>
                                {line.status === 'paid' ? (
                                  <div>
                                    <Badge variant="success">{t('payouts.paid')}</Badge>
                                    {line.paymentMethod && (
                                      <span className="ml-1 text-xs text-gray-500">
                                        {t(`payouts.${line.paymentMethod}`)}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <Select
                                    value=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        markLinePaidMutation.mutate({
                                          payoutId: payout.id,
                                          subSellerId: line.subSellerId,
                                          paymentMethod: e.target.value,
                                        });
                                      }
                                    }}
                                    options={[
                                      { value: '', label: t('payouts.markPaid') },
                                      { value: 'cash', label: t('payouts.cash') },
                                      { value: 'transfer', label: t('payouts.transfer') },
                                      { value: 'crypto', label: t('payouts.crypto') },
                                    ]}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
